import React, { useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowUpRight, CheckCircle2, Phone, MessageCircle, ChevronDown } from "lucide-react";
import Seo, { buildBreadcrumbs } from "@/components/site/Seo";
import { SERVICES, projectsForService } from "@/data/services";
import { faqJsonLd } from "@/components/site/FaqSection";

const PHONE = "07481 626 260";
const PHONE_TEL = "tel:+447481626260";
const WHATSAPP = (msg) => `https://wa.me/447481626260?text=${encodeURIComponent(msg)}`;

export default function ServicePage() {
  const { slug } = useParams();
  const svc = SERVICES[slug];
  if (!svc) return <Navigate to="/services" replace />;
  const projects = projectsForService(slug);

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      serviceType: svc.name,
      name: svc.name,
      description: svc.intro,
      provider: {
        "@type": "GeneralContractor",
        name: "Prime Build Design LTD",
        telephone: "+44 7481 626260",
        email: "primebuilddesignltd9@gmail.com",
        address: {
          "@type": "PostalAddress",
          streetAddress: "45 Bridge Road, Wembley Park",
          addressLocality: "London",
          postalCode: "HA9 9AG",
          addressCountry: "GB",
        },
      },
      areaServed: { "@type": "City", name: "London" },
      offers: {
        "@type": "Offer",
        availability: "https://schema.org/InStock",
        priceCurrency: "GBP",
      },
    },
    buildBreadcrumbs([
      { name: "Services", path: "/services" },
      { name: svc.name, path: `/services/${slug}` },
    ]),
    faqJsonLd(svc.faqs),
  ];

  return (
    <div data-testid={`service-page-${slug}`}>
      <Seo
        title={svc.seoTitle}
        description={svc.metaDescription}
        path={`/services/${slug}`}
        jsonLd={jsonLd}
      />

      {/* HERO */}
      <section className="bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32 grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7">
            <div className="overline mb-6">Service · London</div>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-bold text-slate-900 tracking-tight leading-[1.02]">
              {svc.h1}
            </h1>
            <p className="mt-8 text-lg text-slate-600 max-w-2xl leading-relaxed">{svc.intro}</p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/contact" className="btn-accent" data-testid={`svc-${slug}-cta-quote`}>
                Get a Free Quote <ArrowUpRight size={16} />
              </Link>
              <a href={PHONE_TEL} className="btn-ghost" data-testid={`svc-${slug}-cta-call`}>
                <Phone size={16} /> Call {PHONE}
              </a>
            </div>
          </div>
          {projects[0] && (
            <div className="lg:col-span-5 zoom-img h-[460px]">
              <img src={projects[0].img} alt={`${svc.name} — ${projects[0].title}`} className="w-full h-full object-cover" loading="eager" decoding="async" width="1200" height="800" />
            </div>
          )}
        </div>
      </section>

      {/* BENEFITS */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32 grid lg:grid-cols-2 gap-16 items-start">
          <div>
            <div className="overline mb-4">Why Prime Build</div>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-slate-900 tracking-tight leading-tight">
              What you get when you<br /> work with us.
            </h2>
            <p className="mt-6 text-slate-600 leading-relaxed">
              The same in-house team, the same five-stage process, the same 10-year guarantee — across every service we offer.
            </p>
          </div>
          <ul className="space-y-5">
            {svc.benefits.map((b, i) => (
              <li key={i} className="flex gap-4 border-b border-slate-200 pb-5" data-testid={`svc-${slug}-benefit-${i}`}>
                <CheckCircle2 size={22} className="text-[#C5A059] flex-shrink-0 mt-0.5" />
                <span className="text-slate-800 leading-relaxed">{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* PROCESS */}
      <section className="bg-slate-950 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
          <div className="overline mb-4 text-[#C5A059]">Our process</div>
          <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight max-w-3xl">
            A five-stage process built to remove surprises.
          </h2>
          <div className="mt-16 grid md:grid-cols-5 gap-px bg-slate-800">
            {svc.process.map(([n, t, d], i) => (
              <div key={n} className="bg-slate-950 p-8" data-testid={`svc-${slug}-process-${i}`}>
                <div className="font-display text-[#C5A059] text-sm font-bold">{n}</div>
                <h3 className="mt-3 font-display text-xl font-semibold">{t}</h3>
                <p className="mt-3 text-sm text-slate-400 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* RELATED PROJECTS */}
      {projects.length > 0 && (
        <section className="bg-white border-t border-slate-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-12">
              <div>
                <div className="overline mb-4">Recent {svc.name.toLowerCase()}</div>
                <h2 className="font-display text-4xl md:text-5xl font-bold text-slate-900 tracking-tight">
                  Selected projects.
                </h2>
              </div>
              <Link to="/projects" className="btn-ghost self-start md:self-end" data-testid={`svc-${slug}-view-projects`}>
                View all projects <ArrowUpRight size={16} />
              </Link>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {projects.slice(0, 6).map((p) => (
                <div key={p.id} className="zoom-img relative h-[360px] overflow-hidden" data-testid={`svc-${slug}-project-${p.id}`}>
                  <img src={p.img} alt={p.title} className="w-full h-full object-cover" loading="lazy" decoding="async" />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/85 via-slate-900/15 to-transparent" />
                  <div className="absolute bottom-5 left-5 right-5 text-white">
                    <div className="overline text-white/70">{p.category} · {p.location}</div>
                    <div className="font-display text-lg font-semibold mt-1">{p.title}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
          <div className="overline mb-4">Frequently asked questions</div>
          <h2 className="font-display text-4xl md:text-5xl font-bold text-slate-900 tracking-tight">
            Answers about {svc.name.toLowerCase()}.
          </h2>
          <div className="mt-12 divide-y divide-slate-200">
            {svc.faqs.map((f, i) => (
              <FaqItem key={i} q={f.q} a={f.a} testId={`svc-${slug}-faq-${i}`} />
            ))}
          </div>
        </div>
      </section>

      {/* RELATED SERVICES */}
      {svc.relatedServices && svc.relatedServices.length > 0 && (
        <section className="bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
            <div className="overline mb-4">Related services</div>
            <h2 className="font-display text-3xl font-bold text-slate-900 mb-8">You may also need…</h2>
            <div className="grid md:grid-cols-3 gap-px bg-slate-200">
              {svc.relatedServices.map((rs) => {
                const r = SERVICES[rs];
                if (!r) return null;
                return (
                  <Link key={rs} to={`/services/${rs}`} className="bg-white p-6 hover:bg-slate-50 transition group" data-testid={`svc-related-${rs}`}>
                    <div className="font-display text-2xl font-semibold text-slate-900">{r.name}</div>
                    <div className="text-sm text-slate-600 mt-2 line-clamp-2">{r.intro.split(".")[0]}.</div>
                    <div className="text-sm font-medium mt-4 inline-flex items-center gap-1 text-slate-900 group-hover:text-[#C5A059] transition-colors">
                      Explore <ArrowUpRight size={14} />
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="bg-[#C5A059]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid md:grid-cols-2 gap-10 items-center">
          <h2 className="font-display text-4xl md:text-5xl font-bold text-white tracking-tight leading-tight">
            Ready to start your {svc.name.toLowerCase()}?
          </h2>
          <div className="flex flex-col sm:flex-row gap-4 md:justify-end">
            <Link to="/contact" className="bg-slate-900 text-white px-6 py-4 font-medium inline-flex items-center gap-2 hover:bg-slate-800 transition" data-testid={`svc-${slug}-bottom-quote`}>
              Get a free quote <ArrowUpRight size={18} />
            </Link>
            <a href={WHATSAPP(`Hi Prime Build, I'd like a quote for ${svc.name}.`)} target="_blank" rel="noreferrer" className="bg-white text-slate-900 px-6 py-4 font-medium inline-flex items-center gap-2 hover:bg-slate-50 transition" data-testid={`svc-${slug}-bottom-wa`}>
              <MessageCircle size={18} /> WhatsApp us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

function FaqItem({ q, a, testId }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="py-5" data-testid={testId}>
      <button onClick={() => setOpen((v) => !v)} className="w-full flex justify-between items-start text-left gap-6">
        <span className="font-display text-lg md:text-xl font-semibold text-slate-900">{q}</span>
        <ChevronDown size={20} className={`flex-shrink-0 mt-1 transition-transform ${open ? "rotate-180" : ""} text-slate-500`} />
      </button>
      {open && <p className="mt-4 text-slate-600 leading-relaxed">{a}</p>}
    </div>
  );
}
